import { graphql } from 'react-relay';

import { useViewUserProfile } from '@confluence/global-operations/entry-points/useViewUserProfile.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import {
	type useProfileInfoUserContextQuery$data,
	type useProfileInfoUserContextQuery$variables,
} from './__generated__/useProfileInfoUserContextQuery.graphql.ts';
// Hook which returns info needed for the profile macros
export const useProfileInfo = (
	userId?: string,
): {
	graphqlError: ApolloErrorInterop | undefined;
	hasViewUserProfilePermission: boolean;
	userName: string;
	iconUrl: string;
	email: string;
	jobTitle: string;
	isActiveUser: boolean;
} => {
	const { canViewUserProfile: hasViewUserProfilePermission } = useViewUserProfile();
	const { data, error } = useQueryInterop<
		useProfileInfoUserContextQuery$data,
		useProfileInfoUserContextQuery$variables
	>(
		graphql`
			query useProfileInfoUserContextQuery($userId: ID) {
				user: confluence_atlassianUser(id: $userId) {
					id
					photos {
						value
						isPrimary
					}
					displayName
					emails {
						value
						isPrimary
					}
					title
					isActive
				}
			}
		`,
		{
			variables: {
				userId,
			},
			skip: !userId,
			fetchPolicy: 'cache-first',
		},
	);

	return {
		graphqlError: error,
		hasViewUserProfilePermission,
		userName: data?.user?.displayName ?? '',
		iconUrl: data?.user?.photos?.[0]?.value ?? '',
		email: hasViewUserProfilePermission ? (data?.user?.emails?.[0]?.value ?? '') : '',
		jobTitle: data?.user?.title ?? '',
		isActiveUser: data?.user?.isActive ?? true,
	};
};
